import { useCallback, useEffect, useState } from "react";
import { AdminApiError } from "../../services/api";

/**
 * État d'une zone de la vue d'ensemble.
 *
 * « refusé » et « en échec » sont volontairement deux états distincts : un
 * administrateur qui n'a pas le droit de voir une donnée doit lire qu'elle ne
 * lui est pas ouverte, et non qu'elle est en panne. Aucun des deux ne se replie
 * sur une valeur nulle, qui laisserait croire que la plateforme n'a rien fait.
 */
export type AdminSectionState<T> =
	| { status: "loading" }
	| { status: "ready"; data: T }
	| { status: "forbidden" }
	| { status: "failed"; message: string };

export interface AdminSection<T> {
	state: AdminSectionState<T>;
	reload: () => void;
}

/**
 * Charge une zone indépendamment des autres.
 *
 * Chaque zone échoue pour son propre compte : une permission manquante sur les
 * commandes ne doit pas priver l'administrateur de la population, et une panne
 * d'un point d'entrée ne doit pas vider la page entière.
 */
export function useAdminSection<T>(load: () => Promise<T>, deps: unknown[]): AdminSection<T> {
	const [state, setState] = useState<AdminSectionState<T>>({ status: "loading" });
	const [attempt, setAttempt] = useState(0);

	// La fonction de chargement est recréée à chaque rendu par l'appelant ; ce
	// sont les dépendances déclarées qui décident d'un rechargement.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const run = useCallback(load, deps);

	useEffect(() => {
		let cancelled = false;
		setState({ status: "loading" });

		run()
			.then((data) => {
				if (!cancelled) {
					setState({ status: "ready", data });
				}
			})
			.catch((error: unknown) => {
				if (cancelled) {
					return;
				}
				if (error instanceof AdminApiError && error.status === 403) {
					setState({ status: "forbidden" });
					return;
				}
				setState({
					status: "failed",
					message: error instanceof Error ? error.message : "",
				});
			});

		return () => {
			cancelled = true;
		};
	}, [run, attempt]);

	return { state, reload: () => setAttempt((value) => value + 1) };
}
